<script setup lang="ts">
import { computed, watch } from "vue";

import ActionSource from "@/components/action/ActionSource.vue";
import { useBoardStore } from "@/store/board";
import { useDrawStore } from "@/store/draw";
import { useMessengerStore } from "@/store/messenger";
import { useOnboardingStore } from "@/store/onboarding";
import { usePanModeStore } from "@/store/panMode";
import { usePointerStore } from "@/store/pointer";
import { useSidePanelStore } from "@/store/sidePanel";
import { useUserStore } from "@/store/user";

import AddItem from "./internal/AddItem.vue";
import BottomItems from "./internal/BottomItems.vue";
import CollapsableMenuGroup from "./internal/CollapsableMenuGroup.vue";
import MessengerItem from "./internal/MessengerItem.vue";
import OnboardingMenuItem from "./internal/OnboardingMenuItem.vue";

const onboardingStore = useOnboardingStore();
const drawMode = computed(() => useDrawStore().active);
const showStickyActions = computed(() => useBoardStore().creatableStickyTypes.length > 0);
const showMessenger = computed(() => useMessengerStore().show);

const readonly = computed(() => !useUserStore().isAllowed("edit"));
const panelRightWidth = computed(() => useSidePanelStore().right.width + 10);

const isAnyActive = computed(
  () => usePointerStore().active || drawMode.value || usePanModeStore().active,
);

// When entering draw mode, deactivate the pointer if necessary
watch(
  () => drawMode.value,
  () => {
    if (drawMode.value && usePointerStore().active) {
      usePointerStore().toggle();
    }
  },
);
</script>

<template>
  <ActionSource
    v-scrollable-on-board
    value="board-actions-menu"
    class="bottom-menu"
    :style="{ right: panelRightWidth + 'px' }"
    data-no-screenshot
    :aria-label="$t('label.boardActions')"
  >
    <div class="position right">
      <!-- Add item button -->
      <Transition name="slide-right">
        <div
          v-if="showStickyActions && !readonly && !drawMode"
          class="menu-group menu-group-add-item"
          style="padding: 0"
        >
          <AddItem />
        </div>
      </Transition>

      <!-- Collapsable action group - starts open -->
      <CollapsableMenuGroup class="menu-group actions" :show-marker="isAnyActive">
        <BottomItems />
      </CollapsableMenuGroup>

      <!-- Intercom button -->
      <div v-if="showMessenger" class="menu-group">
        <MessengerItem />
      </div>
    </div>

    <div v-if="onboardingStore.isComplete" class="onboarding-anchor">
      <OnboardingMenuItem />
    </div>
  </ActionSource>
</template>

<style lang="scss" scoped>
@use "@/styles/variables";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/z-index";
@use "@/styles/mixins/shadow";
@use "@/styles/side-panel";

.bottom-menu {
  z-index: z-index.$menu;
  position: fixed;
  bottom: 10px;
  left: 10px;
  right: 10px;
  transition: right ease-in-out side-panel.$panel-transition;

  .position {
    position: absolute;
    bottom: 0;
    display: flex;
    gap: 8px;

    &.center {
      left: 0;
      right: 0;
      margin: auto;
      width: fit-content;
    }

    &.right {
      right: 0;
    }
  }

  .menu-group,
  :deep(.menu-group-inner) {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .menu-group {
    padding: 2px;
    height: 40px;
    background-color: colors.$background-ui;
    border-radius: variables.$common-border-radius;
    width: fit-content;

    &.actions {
      :deep(.base-button) {
        height: variables.$bottom-menu-button-size;
        width: variables.$bottom-menu-button-size;
      }
    }

    @include shadow.default;

    .high-contrast-mode &:not(.menu-group-add-item) {
      border: 2px solid colors-a11y.$divider-color;
    }

    :deep(.delimiter) {
      margin: 0;
    }
  }

  .onboarding-anchor {
    position: absolute;
    bottom: 48px;
    right: 0;
  }

  /**
   * Animation for individual actions (eg. when switching to draw mode)
   * Slides in from the right and fades in
   */
  :deep(.slide-right-enter-active),
  :deep(.slide-right-leave-active) {
    transition: all 0.2s ease-in-out;
  }

  :deep(.slide-right-enter-from),
  :deep(.slide-right-leave-to) {
    max-width: 0;
    opacity: 0;
  }

  :deep(.slide-right-enter-to),
  :deep(.slide-right-leave-from) {
    max-width: 50px;
    opacity: 1;
  }
}
</style>
